import { useEffect, useState } from 'react';
import JSZip from 'jszip';
import { useApp } from '../AppContext';
import { supabase } from '../supabaseClient';

// 9 subfolders. The `key` is what gets persisted to documents.folder_type
// (also constrained at the DB level). Exported because ImportTab's
// Smart Upload classification prompt also needs this list.
export const FOLDERS = [
  { key: 'receipts',      label: 'Receipts',          icon: '📥' },
  { key: 'leases',        label: 'Leases',            icon: '📜' },
  { key: 'property_docs', label: 'Property Docs',     icon: '🏠' },
  { key: 'closing',       label: 'Closing Documents', icon: '💰' },
  { key: 'insurance',     label: 'Insurance',         icon: '🛡️' },
  { key: 'warranties',    label: 'Warranties',        icon: '⚙️' },
  { key: 'tax',           label: 'Tax Documents',     icon: '📊' },
  { key: 'photos',        label: 'Photos',            icon: '📸' },
  { key: 'inspections',   label: 'Inspections',       icon: '🔍' },
];

const STORAGE_FREE_LIMIT = 100 * 1024 * 1024; // 100 MB

function fmtBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  if (n < 1024 * 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + ' MB';
  return (n / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

function sanitizeFileName(name) {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100);
}

// Safe for ZIP/filesystem folder names — strips slashes, colons, etc.
function sanitizePathSegment(name) {
  return (name || 'untitled').replace(/[/\\?%*:|"<>]/g, '-').replace(/\s+/g, ' ').trim().slice(0, 80);
}

export default function DocumentVault({ property, onBack }) {
  const { business, subscription, setActiveTab } = useApp();
  const isPro = subscription?.status === 'pro';

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFolder, setOpenFolder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  // Phase 3: export selection (Set of doc.id) — persists across folder navigation
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(''); // "Downloading 3 of 12…" etc.

  // Load this property's documents on open
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const { data, error: loadErr } = await supabase
        .from('documents')
        .select('*')
        .eq('property_id', property.id)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (loadErr) setError('Could not load documents: ' + loadErr.message);
      else setDocs(data || []);
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [property.id]);

  const folderCounts = FOLDERS.reduce((acc, f) => {
    acc[f.key] = docs.filter(d => d.folder_type === f.key).length;
    return acc;
  }, {});

  const thisPropertyBytes = docs.reduce((s, d) => s + (d.file_size || 0), 0);

  async function handleUpload(file, folderKey) {
    if (!file) return;
    setError('');

    // Trial: check business-wide storage usage before allowing upload
    if (!isPro) {
      const { data: allDocs } = await supabase
        .from('documents')
        .select('file_size')
        .eq('business_id', business.id);
      const businessBytes = (allDocs || []).reduce((s, d) => s + (d.file_size || 0), 0);
      if (businessBytes + file.size > STORAGE_FREE_LIMIT) {
        setError(`Free trial storage limit reached (${fmtBytes(STORAGE_FREE_LIMIT)}). Upgrade to Pro for unlimited storage.`);
        return;
      }
    }

    setUploading(true);
    try {
      const safe = sanitizeFileName(file.name);
      const path = `${business.id}/${property.id}/${folderKey}/${crypto.randomUUID()}-${safe}`;
      const { error: upErr } = await supabase.storage
        .from('property-documents')
        .upload(path, file, { contentType: file.type || 'application/octet-stream' });
      if (upErr) throw new Error(upErr.message);

      const { data: doc, error: insErr } = await supabase
        .from('documents')
        .insert([{
          business_id: business.id,
          property_id: property.id,
          folder_type: folderKey,
          file_name: file.name,
          file_size: file.size,
          storage_path: path,
          mime_type: file.type || null,
        }])
        .select()
        .single();
      if (insErr) {
        // Roll back the storage upload if the metadata insert failed
        await supabase.storage.from('property-documents').remove([path]);
        throw new Error(insErr.message);
      }
      setDocs(prev => [doc, ...prev]);
    } catch (e) {
      setError('Upload failed: ' + e.message);
    } finally {
      setUploading(false);
    }
  }

  // Phase 3: ZIP export. Downloads each selected file from storage, packs them
  // into a folder-structured ZIP, and triggers a browser download.
  async function exportToZip(docsToExport, zipName) {
    if (!docsToExport || docsToExport.length === 0) return;
    setError('');
    setExporting(true);
    try {
      const zip = new JSZip();
      const propertyRootName = sanitizePathSegment(property.name);
      const root = zip.folder(propertyRootName);

      for (let i = 0; i < docsToExport.length; i++) {
        const doc = docsToExport[i];
        setExportStatus(`Downloading ${i + 1} of ${docsToExport.length}…`);
        const { data: blob, error: dlErr } = await supabase.storage
          .from('property-documents')
          .download(doc.storage_path);
        if (dlErr || !blob) {
          console.warn('Skipping failed download:', doc.file_name, dlErr?.message);
          continue;
        }
        const folderLabel = FOLDERS.find(f => f.key === doc.folder_type)?.label || doc.folder_type;
        const sub = root.folder(sanitizePathSegment(folderLabel));

        // Handle filename collisions inside the same subfolder by appending "(2)", "(3)", ...
        let fileName = doc.file_name || 'untitled';
        if (sub.file(fileName)) {
          const dot = fileName.lastIndexOf('.');
          const base = dot > 0 ? fileName.slice(0, dot) : fileName;
          const ext  = dot > 0 ? fileName.slice(dot)   : '';
          let counter = 2;
          while (sub.file(`${base} (${counter})${ext}`)) counter++;
          fileName = `${base} (${counter})${ext}`;
        }
        sub.file(fileName, blob);
      }

      setExportStatus('Creating ZIP…');
      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${propertyRootName} - ${zipName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Export failed: ' + e.message);
    } finally {
      setExporting(false);
      setExportStatus('');
    }
  }

  async function exportSelected() {
    const docsToExport = docs.filter(d => selectedIds.has(d.id));
    await exportToZip(docsToExport, `${docsToExport.length} files`);
  }

  async function exportAll() {
    await exportToZip(docs, 'Full Vault');
  }

  function toggleSelect(docId) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function handleView(doc) {
    setError('');
    const { data, error: sigErr } = await supabase.storage
      .from('property-documents')
      .createSignedUrl(doc.storage_path, 3600);
    if (sigErr || !data?.signedUrl) {
      setError('Could not open file: ' + (sigErr?.message || 'no URL'));
      return;
    }
    // Anchor-click pattern. We previously used window.open('about:blank',
    // '_blank', 'noopener,noreferrer') synchronously to dodge popup
    // blockers, then awaited the signed URL and tried to set
    // newTab.location.href. The problem: passing `noopener` to
    // window.open returns null per spec (the opener handle is severed),
    // so `newTab` was always null and the fallback branch ran
    // `window.location.href = signedUrl` — which navigated LandlordLedger
    // away in the original tab AND left the blank tab orphaned.
    //
    // Anchor click avoids both issues: it opens a real new tab with
    // target=_blank + rel=noopener, never touches the current tab, and
    // browsers treat it as user-initiated (rarely popup-blocked) even
    // after a short await.
    const a = document.createElement('a');
    a.href = data.signedUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async function handleDelete(doc) {
    if (!window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return;
    setError('');
    const { error: rmErr } = await supabase.storage
      .from('property-documents')
      .remove([doc.storage_path]);
    if (rmErr) { setError('Could not remove file: ' + rmErr.message); return; }
    await supabase.from('documents').delete().eq('id', doc.id);
    setDocs(prev => prev.filter(d => d.id !== doc.id));
  }

  const headerStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 10 };
  const backButtonStyle = { background: 'transparent', border: '1px solid #2d3555', borderRadius: 8, padding: '6px 12px', color: '#94a3b8', cursor: 'pointer', fontSize: 12 };
  const folderTileStyle = { background: '#0f1117', border: '1px solid #1e2235', borderRadius: 12, padding: '18px 12px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s' };

  return (
    <div style={{ background: '#0a0d16', border: '1px solid #3b82f6', borderRadius: 14, padding: '14px 16px' }}>
      <div style={headerStyle}>
        <button onClick={openFolder ? () => setOpenFolder(null) : onBack} style={backButtonStyle}>
          ← {openFolder ? 'All Folders' : 'Back to Property'}
        </button>
        <div style={{ flex: 1, textAlign: 'right' }}>
          <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, lineHeight: 1.2 }}>{property.name}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'Courier New', monospace", letterSpacing: 0.5 }}>DOCUMENT VAULT</div>
        </div>
      </div>

      {/* Storage usage line */}
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
        Stored for this property: <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{fmtBytes(thisPropertyBytes)}</span>
        {!isPro && (
          <> · Free trial cap: {fmtBytes(STORAGE_FREE_LIMIT)} across all properties · <span onClick={() => setActiveTab('billing')} style={{ color: '#3b82f6', cursor: 'pointer' }}>Upgrade →</span></>
        )}
      </div>

      {error && (
        <div style={{ background: '#2d1515', border: '1px solid #7f1d1d', borderRadius: 8, padding: '10px 12px', color: '#f87171', fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Selection bar — visible from any folder so cross-folder selections are obvious */}
      {selectedIds.size > 0 && !exporting && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#0f1a2e', border: '1px solid #1e3a5f', borderRadius: 10, padding: '8px 12px', marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, color: '#93c5fd', fontWeight: 600, flex: 1, minWidth: 100 }}>
            ✓ {selectedIds.size} file{selectedIds.size !== 1 ? 's' : ''} selected
          </div>
          <button onClick={clearSelection}
            style={{ background: 'transparent', border: '1px solid #2d3555', borderRadius: 8, padding: '6px 12px', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }}>
            Clear
          </button>
          <button onClick={exportSelected}
            style={{ background: '#1d4ed8', border: 'none', borderRadius: 8, padding: '6px 14px', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            📦 Export ZIP
          </button>
        </div>
      )}

      {/* Export in-progress banner */}
      {exporting && (
        <div style={{ background: '#1a2540', border: '1px solid #3b82f6', borderRadius: 10, padding: '10px 12px', marginBottom: 12, color: '#93c5fd', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 14, height: 14, border: '2px solid #1e3a5f', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.9s linear infinite', flexShrink: 0 }} />
          <span>{exportStatus || 'Exporting…'}</span>
        </div>
      )}

      {loading ? (
        <div style={{ color: '#94a3b8', fontSize: 13, padding: '24px 0', textAlign: 'center', fontFamily: "'Courier New', monospace" }}>Loading documents…</div>
      ) : !openFolder ? (
        <>
          {/* Vault home: tip + Export All + folder grid. AI classification now lives on the Import tab. */}
          {docs.length > 0 ? (
            <button
              onClick={exportAll}
              disabled={exporting}
              style={{ width: '100%', background: '#0f1117', border: '1px solid #2d3555', borderRadius: 12, padding: '10px 0', color: exporting ? '#475569' : '#e2e8f0', cursor: exporting ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
              📦 Export All Files ({docs.length}) as ZIP
            </button>
          ) : (
            <div style={{ background: '#0f1117', border: '1px solid #1e2235', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
              💡 Tip: use the <strong style={{ color: '#3b82f6', cursor: 'pointer' }} onClick={() => setActiveTab('import')}>Import tab</strong> to let AI file documents automatically, or pick a folder below to upload directly.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
            {FOLDERS.map(f => (
              <div key={f.key} onClick={() => setOpenFolder(f.key)} style={folderTileStyle}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>{f.icon}</div>
                <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600, marginBottom: 2 }}>{f.label}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', fontFamily: "'Courier New', monospace" }}>{folderCounts[f.key]} {folderCounts[f.key] === 1 ? 'file' : 'files'}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        // File list inside a folder
        <FolderView
          folder={FOLDERS.find(f => f.key === openFolder)}
          files={docs.filter(d => d.folder_type === openFolder)}
          uploading={uploading}
          onUpload={file => handleUpload(file, openFolder)}
          onView={handleView}
          onDelete={handleDelete}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          onToggleSelect={toggleSelect}
        />
      )}
    </div>
  );
}

function FolderView({ folder, files, uploading, onUpload, onView, onDelete, selectedIds, setSelectedIds, onToggleSelect }) {
  const fileInputId = `vault-file-${folder.key}`;
  const cameraInputId = `vault-camera-${folder.key}`;
  const showCamera = folder.key === 'photos' || folder.key === 'receipts' || folder.key === 'inspections';
  const allInFolderSelected = files.length > 0 && files.every(f => selectedIds.has(f.id));

  function toggleFolderSelection() {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allInFolderSelected) {
        files.forEach(f => next.delete(f.id));
      } else {
        files.forEach(f => next.add(f.id));
      }
      return next;
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 22 }}>{folder.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, color: '#e2e8f0', fontWeight: 600 }}>{folder.label}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'Courier New', monospace" }}>{files.length} {files.length === 1 ? 'file' : 'files'}</div>
        </div>
        {files.length > 0 && (
          <button onClick={toggleFolderSelection}
            style={{ background: '#1e2235', border: '1px solid #2d3555', borderRadius: 8, padding: '6px 12px', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }}>
            {allInFolderSelected ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      {/* Upload controls */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          id={fileInputId}
          type="file"
          style={{ display: 'none' }}
          onChange={e => { const f = e.target.files[0]; if (f) onUpload(f); e.target.value = ''; }}
        />
        <button
          onClick={() => document.getElementById(fileInputId).click()}
          disabled={uploading}
          style={{ flex: 1, minWidth: 140, background: uploading ? '#1e2235' : '#1d4ed8', border: 'none', borderRadius: 10, padding: '10px 14px', color: '#fff', cursor: uploading ? 'wait' : 'pointer', fontSize: 14, fontWeight: 600 }}>
          {uploading ? 'Uploading…' : '📤 Upload File'}
        </button>
        {showCamera && (
          <>
            <input
              id={cameraInputId}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={e => { const f = e.target.files[0]; if (f) onUpload(f); e.target.value = ''; }}
            />
            <button
              onClick={() => document.getElementById(cameraInputId).click()}
              disabled={uploading}
              style={{ flex: 1, minWidth: 140, background: '#0f1117', border: '1px solid #2d3555', borderRadius: 10, padding: '10px 14px', color: '#e2e8f0', cursor: uploading ? 'wait' : 'pointer', fontSize: 14, fontWeight: 600 }}>
              📸 Take Photo
            </button>
          </>
        )}
      </div>

      {/* File list */}
      {files.length === 0 ? (
        <div style={{ color: '#475569', fontSize: 13, padding: '24px 0', textAlign: 'center', fontFamily: "'Courier New', monospace" }}>
          No files in this folder yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {files.map(doc => {
            const isSelected = selectedIds.has(doc.id);
            return (
              <div key={doc.id}
                style={{ background: isSelected ? '#0f1a2e' : '#0f1117', border: `1px solid ${isSelected ? '#3b82f6' : '#1e2235'}`, borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div onClick={() => onToggleSelect(doc.id)}
                  style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${isSelected ? '#3b82f6' : '#2d3555'}`, background: isSelected ? '#3b82f6' : 'transparent', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isSelected && <span style={{ color: '#fff', fontSize: 13, lineHeight: 1 }}>✓</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</div>
                  <div style={{ fontSize: 11, color: '#64748b', fontFamily: "'Courier New', monospace" }}>
                    {fmtBytes(doc.file_size)} · {new Date(doc.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button onClick={() => onView(doc)} style={{ background: '#1e2235', border: '1px solid #2d3555', borderRadius: 8, padding: '6px 12px', color: '#93c5fd', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>View</button>
                <button onClick={() => onDelete(doc)} title="Delete" style={{ background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>✕</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

